import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import { fetchControllers } from '@/features/controllers/api'
import { BackToDetailsLink } from '@/features/deviceDetails/BackToDetailsLink'
import {
  createMaintenanceNote,
  deleteMaintenanceNote,
  fetchMaintenanceNotes,
  updateMaintenanceNote,
} from '@/features/maintenanceNotes/api'
import LoadingState from '@/components/common/LoadingState'
import EmptyState from '@/components/common/EmptyState'
import ErrorState from '@/components/common/ErrorState'

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

const now = new Date()

/**
 * Ports includes/maintenance_management.html. The legacy "all controllers"
 * mode (no controller selected) is a client-side stub in the original app
 * (loadAllNotes() just clears the list — no cross-controller endpoint
 * exists), so this page requires picking a controller first, same as the
 * legacy page's actual (non-stub) behavior. A `?controller_id=` query param
 * preselects the controller, matching the legacy per-device sidebar's
 * direct link (`/portal/controllers/maintenance/<controller_id>`).
 */
export default function MaintenanceNotesPage() {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('controllers.notes.manage')
  const queryClient = useQueryClient()

  const controllersQuery = useQuery({ queryKey: ['controllers'], queryFn: fetchControllers })
  // Read directly from the URL, not frozen into local state at mount -- see
  // SettingsAlarmsPage's matching comment for the real bug this fixes.
  const [searchParams, setSearchParams] = useSearchParams()
  const controllerId = searchParams.get('controller_id') ?? ''
  // The site the user actually navigated here FROM -- see SettingsAlarmsPage's
  // matching comment. "← Back to site details" always returns to this one,
  // never whichever site the dropdown currently shows.
  const [originalControllerId] = useState(() => searchParams.get('controller_id') ?? '')
  const [year, setYear] = useState<number | ''>('')
  const [month, setMonth] = useState<number | ''>('')

  const notesQuery = useQuery({
    queryKey: ['maintenance-notes', controllerId, year, month],
    queryFn: () => fetchMaintenanceNotes(controllerId, year || undefined, month || undefined),
    enabled: Boolean(controllerId),
  })

  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [noteText, setNoteText] = useState('')
  const [noteYear, setNoteYear] = useState(now.getFullYear())
  const [noteMonth, setNoteMonth] = useState(now.getMonth() + 1)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['maintenance-notes', controllerId] })

  const createMutation = useMutation({
    mutationFn: () => createMaintenanceNote({ controller_id: controllerId, note: noteText, year: noteYear, month: noteMonth }),
    onSuccess: () => {
      setEditingId(null)
      setNoteText('')
      invalidate()
    },
  })
  const updateMutation = useMutation({
    mutationFn: (id: number) => updateMaintenanceNote(id, { note: noteText, year: noteYear, month: noteMonth }),
    onSuccess: () => {
      setEditingId(null)
      setNoteText('')
      invalidate()
    },
  })
  const deleteMutation = useMutation({ mutationFn: deleteMaintenanceNote, onSuccess: invalidate })

  function startCreate() {
    setNoteText('')
    setNoteYear(now.getFullYear())
    setNoteMonth(now.getMonth() + 1)
    setEditingId('new')
  }

  return (
    <div className="space-y-4">
      <BackToDetailsLink controllerId={originalControllerId} />
      <h1 className="text-xl font-semibold text-text-primary">Maintenance Notes</h1>

      <div className="flex flex-wrap items-end gap-3 rounded border border-border bg-card p-4">
        <div className="max-w-full">
          <label className="mb-1 block text-xs text-text-secondary">Controller</label>
          <select
            value={controllerId}
            onChange={(e) => {
              setSearchParams(e.target.value ? { controller_id: e.target.value } : {})
              setEditingId(null)
            }}
            className="max-w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
          >
            <option value="">Select a controller…</option>
            {(controllersQuery.data ?? []).map((c) => (
              <option key={c.controller_id} value={c.controller_id}>
                {c.controller_name || c.controller_id}
              </option>
            ))}
          </select>
        </div>
        {controllerId && (
          <>
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Year</label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value ? Number(e.target.value) : '')}
                className="w-24 rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Month</label>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : '')}
                className="rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
              >
                <option value="">All</option>
                {MONTH_NAMES.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            {(year !== '' || month !== '') && (
              <button
                type="button"
                onClick={() => {
                  setYear('')
                  setMonth('')
                }}
                className="text-xs text-text-secondary hover:underline"
              >
                Clear filters
              </button>
            )}
          </>
        )}
      </div>

      {!controllerId && <EmptyState message="Select a controller to view its maintenance notes." />}

      {controllerId && canManage && editingId === null && (
        <button
          type="button"
          onClick={startCreate}
          className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          + Add Maintenance Note
        </button>
      )}

      {editingId !== null && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (noteText.trim().length < 5) return
            if (editingId === 'new') createMutation.mutate()
            else updateMutation.mutate(editingId)
          }}
          className="space-y-3 rounded border border-brand bg-card p-4"
        >
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Note (at least 5 characters)"
            rows={3}
            className="w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
          />
          <div className="flex gap-3">
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Year</label>
              <input
                type="number"
                value={noteYear}
                onChange={(e) => setNoteYear(Number(e.target.value))}
                className="w-24 rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Month</label>
              <select
                value={noteMonth}
                onChange={(e) => setNoteMonth(Number(e.target.value))}
                className="rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
              >
                {MONTH_NAMES.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={noteText.trim().length < 5 || createMutation.isPending || updateMutation.isPending}
              className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditingId(null)}
              className="rounded border border-border px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {controllerId && notesQuery.isLoading && <LoadingState label="Loading notes…" />}
      {controllerId && notesQuery.isError && (
        <ErrorState
          message={notesQuery.error instanceof Error ? notesQuery.error.message : 'Failed to load notes.'}
          onRetry={() => notesQuery.refetch()}
        />
      )}
      {controllerId && notesQuery.data && notesQuery.data.length === 0 && (
        <EmptyState message="No maintenance notes for this period." />
      )}

      {controllerId && notesQuery.data && notesQuery.data.length > 0 && (
        <div className="space-y-2">
          {notesQuery.data.map((n) => (
            <div key={n.id} className="rounded border border-border bg-card p-3">
              <div className="mb-1 flex items-center justify-between text-xs text-text-secondary">
                <span>
                  {MONTH_NAMES[n.month - 1]} {n.year}
                  {n.created_by && ` · ${n.created_by}`}
                </span>
                {canManage && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setNoteText(n.note)
                        setNoteYear(n.year)
                        setNoteMonth(n.month)
                        setEditingId(n.id)
                      }}
                      className="text-accent hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('Delete this note?')) deleteMutation.mutate(n.id)
                      }}
                      className="text-error hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
              <p className="text-sm text-text-primary">{n.note}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
